import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataSourceType from '../DataSourceType';
import { Accordion, AccordionDetails, AccordionSummary } from '../components/Accordian';
import AddCustomFieldDialog from '../components/AddCustomFieldDialog';
import TemplateJsonEditor from '../components/TemplateJsonEditor';
import { ReactComponent as ArrowDownIcon } from '../resources/images/arrow_down.svg';
import { WizardStore } from '../stores/WizardStore';
import ConfigItem from './ConfigItem';
import FailedMetricEditor from './FailedMerticEditor';
import MappingItem, { LogicalItem, MappingItemProps } from './MappingItem';
import MultipleAssignmentsEditor from './MultipleAssignmentsEditor';
import { ConfirmationDialogProps } from './StepConfirmation';
import TriageProductSelect from './TriageProductSelect';
import './Wizard.scss';

export type FieldMetadata = {
    value: string;
    displayName: string;
    getEntriesUrl?: string;
    subEntries?: FieldMetadata[];
};

export type ProductMapping = {
    siName?: Array<string>;
    nlpName?: string;
    nlpId?: number;
};

type DataSettingsProps = {
    wizardStore: WizardStore;
    row_id?: String;
    dataSettings?: any;
};

export const DataSettingsSteps = {
    TemplateEditor: 'step1',
    Mapping: 'step2',
    Config: 'step3',
    Triage: 'step4',
    Nlp: 'step5',
};

export const CategoryTexts = {
    custom: {
        title: 'Custom Fields',
        desc: 'Custom fields will not appear on SI or be part of its calculations',
    },
};

export const DataSettingsTypes = {
    Mapping: 'mapping',
    Config: 'config',
    Triage: 'triage',
    Nlp: 'nlp',
};

export const RenderKey = {
    FailedMetricEditor,
    TriageProductSelect,
};

export const shouldDisplayMultipleAssignments = (
    a?: { [value: string]: MappingItemProps[] },
    b?: { [value: string]: MappingItemProps[] },
) => {
    if (!(a && b)) {
        return true;
    }

    let eq = false;

    for (const value in a) {
        if (a[value]) {
            const [loaded, current] = [b[value], a[value]];

            if (loaded && current && !eq) {
                if (current.length > loaded.length) {
                    eq = true;
                    break;
                }

                const currentNames = current.map((item) => item.name).sort();
                const loadedNames = loaded.map((item) => item.name).sort();

                for (let i = 0; i < currentNames.length; i++) {
                    if (currentNames[i] !== loadedNames[i]) {
                        eq = true;
                        break;
                    }
                }
            } else {
                eq = true;
                break;
            }
        }
    }

    return eq;
};

const getResultByLogicalExpression = (container: MappingItemProps[], item: MappingItemProps) => {
    const output: boolean[] = [];
    for (const dependency of item.dependencies!.dependencyValues) {
        output.push(container.some((depend) => depend.name === dependency && depend.value !== ''));
    }
    let result = false;

    if (item.dependencies?.logicalContext === LogicalItem.OR) {
        result = output.some((item) => item === true);
    } else {
        result = output.every((item) => item === true);
    }

    return result;
};

export const isDependentConfigItemsValid = (mapping: MappingItemProps[]) => {
    let output = true;
    if (mapping) {
        for (const item of mapping) {
            if (!item.dependencies || item.value === '') {
                continue;
            }
            const dependencyResult = getResultByLogicalExpression(mapping, item);

            if (dependencyResult !== true) {
                output = false;
                break;
            }
        }
    }
    return output;
};

export const getDataSettingsConfirmProps = (
    LoadedConfigMultipleAssignments: { [value: string]: MappingItemProps[] },
    mappingValid: () => boolean,
    handleNext: () => void,
    getMultipleAssignments: () => { [value: string]: MappingItemProps[] },
    clearAssignment: (item: MappingItemProps) => void,
    saveAssignments: () => void,
    cancelAssignments: () => void,
) => {
    const res: ConfirmationDialogProps[] = [];

    const multipleAssignments = getMultipleAssignments();

    if (multipleAssignments) {
        const multiAssignConfirmProps: ConfirmationDialogProps = {
            title: 'Are you sure?',
            content: (
                <MultipleAssignmentsEditor
                    multipleAssignments={multipleAssignments}
                    clearAssignment={clearAssignment}
                />
            ),
            onYes: () => {
                saveAssignments();
                cancelAssignments();
                if (mappingValid()) {
                    handleNext();
                }
            },
            yesLabel: 'Confirm',
            onClose: cancelAssignments,
            closeLabel: 'Cancel',
        };

        // compare with loaded multi assigned
        const shouldDisplay = shouldDisplayMultipleAssignments(multipleAssignments, LoadedConfigMultipleAssignments);

        if (shouldDisplay) {
            res.push(multiAssignConfirmProps);
        }
    }

    return res;
};

const RenderSearch = (props: any) => (
    <TextField
        className="RenderSearchText"
        placeholder="Search by keyword"
        onChange={(e) => {
            props.setSearchValue(e.target.value);
        }}
        value={props.searchValue}
        InputProps={{
            style: {
                width: '250px',
                height: '38px',
            },
            startAdornment: (
                <InputAdornment position="start" className="RenderSearchIcon">
                    <SearchIcon />
                </InputAdornment>
            ),
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={{
            'data-testid': 'data-settings-search-textfield',
        }}
    />
);

const delay = (ms) =>
    new Promise((res) => {
        setTimeout(res, ms);
    });

export const DataSettings = observer(({ wizardStore }: DataSettingsProps) => {
    const { configStore } = wizardStore;
    const { dataSettings, template_json, setTemplateJson, template_config, setDataSettings } = configStore;
    const setItem = configStore.setDataSettingsItem;
    const { t } = useTranslation();
    const { oktaAuth } = useOktaAuth();
    const { showProgress } = configStore;
    const { expandedTab, setExpandedTab, setOpenDateSelection } = wizardStore;
    const { setActiveStep } = wizardStore;
    const [expanded, setExpanded] = useState<string | false>();
    const { mappingTargets, setMappingTargets } = wizardStore;
    const { duplicateMappingLabels, setDuplicateMappingLabels } = wizardStore;
    const [searchValue, setSearchValue] = useState<string>('');
    const [dataSettingFiltered, setDataSettingFiltered] = useState([]);
    const [isTemplateExpanded, setIsTemplateExpanded] = useState<boolean>(false);
    const templateEditorRef = useRef<HTMLInputElement>();
    const fieldsMappingRef = useRef<HTMLInputElement>();
    const dataConfigurationRef = useRef<HTMLInputElement>();
    const triageRef = useRef<HTMLInputElement>();
    const [isAddCustomFieldOpen, setIsAddCustomFieldOpen] = useState<boolean>(false);
    const [mappingFieldsNames, setMappingFieldsNames] = useState<string[]>([]);
    const [mappedTargets, setMappedTargets] = useState<string[]>([]);
    const mappingItems = dataSettings?.config_mapping[DataSettingsTypes.Mapping];
    const triageItems = dataSettings?.config_mapping[DataSettingsTypes.Triage];
    const configItems = dataSettings?.config_mapping[DataSettingsTypes.Config];

    useEffect(() => {
        if (dataSettings) {
            getDuplicateLabels(mappingItems);
        }
    }, [dataSettings]);

    useEffect(() => {
        if (expandedTab) {
            let tempRef;
            let expanded = expandedTab;

            switch (expandedTab) {
                case DataSettingsSteps.TemplateEditor:
                    tempRef = templateEditorRef;
                    break;
                case DataSettingsSteps.Mapping:
                    tempRef = fieldsMappingRef;
                    break;
                case DataSettingsSteps.Config:
                    tempRef = dataConfigurationRef;
                    break;
                case DataSettingsSteps.Triage:
                    tempRef = triageRef;
                    break;
                default:
                    tempRef = templateEditorRef;
                    expanded = DataSettingsSteps.TemplateEditor;
            }

            scrollTo(tempRef, expanded);
        } else {
            scrollTo(templateEditorRef, DataSettingsSteps.TemplateEditor);
        }
    }, [expandedTab]);

    useEffect(() => {
        if (expanded !== false) {
            if (getDataSettingTypeByStep(expanded) && dataSettings) {
                setDataSettingFiltered(
                    dataSettings.config_mapping[getDataSettingTypeByStep(expanded)].filter((item) =>
                        item.name.toLowerCase().includes(searchValue.toLowerCase()),
                    ),
                );

                const mappingFieldsNames = [];
                const mappedTargets = [];

                mappingItems.forEach((mappingItem) => {
                    mappingFieldsNames.push(mappingItem.name);
                    if (mappingItem.value !== '') {
                        mappedTargets.push(mappingItem.value);
                    }
                });

                setMappingFieldsNames(mappingFieldsNames);
                setMappedTargets(mappedTargets);
            }
        }
    }, [searchValue, expanded, dataSettings]);

    useEffect(() => {
        if (template_json) {
            try {
                setMappingTargets([]);
                const parsed_template_json = JSON.parse(template_json);
                setMappingTargets(findTargets(parsed_template_json));
            } catch (e) {
                console.log(e);
            }
        }
    }, [template_json]);

    const getDuplicateLabels = (mappingItems: MappingItemProps[]) => {
        const labelItems = mappingItems
            .filter((item) => item.value !== '' && item.label !== '')
            .map((item) => item.label);
        const dupLabels = labelItems.filter((e, i, a) => a.indexOf(e) !== i);
        if (dupLabels !== undefined && dupLabels.length >= 0) {
            setDuplicateMappingLabels(dupLabels as string[]);
        }
    };

    const onDeleteCustomField = (fieldName: string) => {
        const copyDataSetting = _.cloneDeep(dataSettings);
        const newMappingItems = _.cloneDeep(mappingItems.filter((item) => item.name !== fieldName));
        copyDataSetting.config_mapping[DataSettingsTypes.Mapping] = newMappingItems;
        setDataSettings(copyDataSetting);
    };

    const findTargets = (template: any) => {
        const targets = [];
        template.rootObjects.forEach((root) => {
            targets.push(...root.content.mapping.filter((item) => item.target !== '').map((item) => item.target));
            root.childObjects.forEach((child) => {
                targets.push(...child.mapping.filter((item) => item.target !== '').map((item) => item.target));
            });
        });
        // search in post process
        if (template.postProcess?.aquantCase?.target) {
            targets.push(template.postProcess.aquantCase.target);
        }

        if (template.postProcess?.nlp?.observationTarget && template.postProcess?.nlp?.observationTarget !== '') {
            targets.push(template.postProcess.nlp.observationTarget);
        }

        if (template.postProcess?.nlp?.solutionTarget && template.postProcess?.nlp?.solutionTarget !== '') {
            targets.push(template.postProcess.nlp.solutionTarget);
        }

        return Array.from(new Set(targets)).sort();
    };

    const mappingSelectedValHasUnmatchedTarget = (value: string) => {
        if (value !== '' && !mappingTargets.includes(value)) {
            return true;
        }
        return false;
    };

    const groupedMappingItemsByCategory = (dataSettingFiltered: MappingItemProps[]) =>
        Object.entries(
            dataSettingFiltered.reduce(
                (groupedMappingItems, item: MappingItemProps) => {
                    if (!item.category) {
                        item.category = '';
                    }

                    // If the category is not yet a key in the accumulator object, create it and set its value to an empty array
                    if (!groupedMappingItems[item.category]) {
                        groupedMappingItems[item.category] = [];
                    }

                    groupedMappingItems[item.category].push(item);
                    return groupedMappingItems;
                },
                {} as { [key: string]: MappingItemProps[] },
            ),
        ).map(([category, items]) => ({ category, items }));

    const noResultComponent = (
        <Typography variant="body2" className="DescriptionContainer">
            {' '}
            No Results{' '}
        </Typography>
    );

    const MappingItemsComponent = (
        <Stack>
            {groupedMappingItemsByCategory(dataSettingFiltered).map((group, index) => (
                <Stack key={index} className="TabContainer" direction="column" width="100%" spacing={1}>
                    {group.category ? (
                        <Stack direction="row">
                            <Typography fontSize="16px" fontWeight="500" marginRight="15px">
                                {CategoryTexts[group.category].title}
                            </Typography>
                            <Typography fontStyle="italic" fontSize="11px" fontWeight="400">
                                {CategoryTexts[group.category].desc}
                            </Typography>
                        </Stack>
                    ) : null}
                    {group.items.map((item) => (
                        <MappingItem
                            onReject={() => {
                                setItemInternal(DataSettingsTypes.Mapping, { ...item, value: '' });
                            }}
                            deleteCustomField={onDeleteCustomField}
                            key={item.name}
                            {...item}
                            setLabel={(l: string) => {
                                setItemInternal(DataSettingsTypes.Mapping, { ...item, label: l });
                                getDuplicateLabels(mappingItems);
                            }}
                            isLabelDup={(l: string) => l !== '' && duplicateMappingLabels.includes(l!)}
                            clearLabel={() => {
                                setItemInternal(DataSettingsTypes.Mapping, { ...item, label: '' });
                                getDuplicateLabels(mappingItems);
                            }}
                            renderDropdown={(cb) => (
                                <FormControl
                                    error={mappingSelectedValHasUnmatchedTarget(item.value)}
                                    sx={{ minWidth: 200 }}
                                >
                                    <Select
                                        className="MappingItemSelect"
                                        value={item.value}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (item.value === '') {
                                                return <em>Select value</em>;
                                            }

                                            return item.value;
                                        }}
                                        inputProps={{
                                            'data-testid': `data-settings-mapping-field-select-${item.name}`,
                                        }}
                                        onChange={(e) => {
                                            setItemInternal(DataSettingsTypes.Mapping, {
                                                ...item,
                                                value: e.target.value,
                                            });
                                            getDuplicateLabels(mappingItems);
                                        }}
                                    >
                                        {mappingTargets.map((target: string, index) => (
                                            <MenuItem key={index} value={target}>
                                                {target}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {mappingSelectedValHasUnmatchedTarget(item.value) && (
                                        <FormHelperText>This value doesn't exists in the template</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    ))}
                </Stack>
            ))}
            {dataSettingFiltered.length === 0 && noResultComponent}
        </Stack>
    );

    const getDataSettingTypeByStep = (step: string) => {
        let dataSettingsType = null;
        const dataSettingsStepName = Object.keys(DataSettingsSteps).find((type) => DataSettingsSteps[type] === step);
        if (dataSettingsStepName && DataSettingsTypes[dataSettingsStepName]) {
            dataSettingsType = DataSettingsTypes[dataSettingsStepName];
        }

        return dataSettingsType;
    };

    const handleTemplateTestRun = async () => {
        configStore.setTemplateTestRun(true);
        if (configStore.source_type.name === DataSourceType.csv) {
            showProgress(true);
            await configStore.createConfig(oktaAuth);
            await configStore.saveDataSource(oktaAuth);
            await configStore.handleRunTemplateTest(
                oktaAuth,
                () => {
                    setActiveStep(wizardStore.activeStep + 1);
                    showProgress(false);
                },
                () => showProgress(false),
            );
        } else {
            // CRM - choose dates first
            setOpenDateSelection(true);
        }
    };

    const handleStepChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
        setSearchValue('');
        if (getDataSettingTypeByStep(panel)) {
            setDataSettingFiltered(dataSettings.config_mapping[getDataSettingTypeByStep(panel)]);
        }
        setExpanded(newExpanded ? panel : false);
    };

    const onAddCustomField = (customFields: string[]) => {
        const copyMappingItems = _.cloneDeep(mappingItems);
        const copyDataSettings = _.cloneDeep(dataSettings);

        customFields.forEach((field) => {
            const customMappingItem: MappingItemProps = {
                name: field,
                type: 'string',
                category: 'custom',
                value: field,
                isRequired: false,
                label: '',
            };

            copyMappingItems.push(customMappingItem);
            copyDataSettings.config_mapping[DataSettingsTypes.Mapping] = copyMappingItems;
            setDataSettings(copyDataSettings);
            setIsAddCustomFieldOpen(false);
        });
    };

    // called from the UI editor
    const setItemInternal = (dataSettingsType: string, item: MappingItemProps) => {
        setItem(dataSettingsType, item);
    };

    const totalSteps = 4;

    const scrollTo = async (ref, step) => {
        if (ref && ref.current) {
            setExpanded(step);
            await delay(1000);
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <Stack className="DataSettings" direction="column">
            {
                <Box sx={{ flexGrow: 1 }}>
                    <div ref={templateEditorRef} />
                    <Accordion
                        expanded={expanded === DataSettingsSteps.TemplateEditor}
                        onChange={handleStepChange(DataSettingsSteps.TemplateEditor)}
                    >
                        <AccordionSummary
                            expanded={expanded === DataSettingsSteps.TemplateEditor}
                            id={DataSettingsSteps.TemplateEditor}
                            title={t('data-settings.template-editor')}
                            thisStep={1}
                            totalSteps={totalSteps}
                            stepTitle={t('common.accordian-step-title')}
                        />
                        <AccordionDetails>
                            {!template_config && <CircularProgress data-testid="CircularProgress" />}
                            {template_config ? (
                                <Stack direction="column">
                                    <Typography variant="body1" marginBottom="20px" className="DescriptionContainer">
                                        {t('data-settings.template-editor-description')}
                                    </Typography>
                                    {isTemplateExpanded ? (
                                        <Dialog open fullScreen>
                                            <TemplateJsonEditor
                                                setIsTemplateExpanded={() => setIsTemplateExpanded(!isTemplateExpanded)}
                                                config={template_config}
                                                progress={showProgress}
                                                hasSaveBtn={false}
                                                setParentTemplateJson={setTemplateJson}
                                            />
                                        </Dialog>
                                    ) : null}
                                    {!isTemplateExpanded && (
                                        <TemplateJsonEditor
                                            setIsTemplateExpanded={() => setIsTemplateExpanded(!isTemplateExpanded)}
                                            config={template_config}
                                            progress={showProgress}
                                            hasSaveBtn={false}
                                            setParentTemplateJson={setTemplateJson}
                                        />
                                    )}
                                </Stack>
                            ) : null}
                            <Stack direction="row" marginTop="32px" justifyContent="center">
                                <Button
                                    className="TestTemplateBtn"
                                    data-testid="data-settings-test-template-btn"
                                    disabled={!mappingTargets || mappingTargets.length === 0}
                                    onClick={() => handleTemplateTestRun()}
                                >
                                    {t('data-settings.test-template')}
                                </Button>
                                <Button
                                    onClick={async () => {
                                        setDataSettingFiltered(mappingItems);
                                        scrollTo(fieldsMappingRef, DataSettingsSteps.Mapping);
                                    }}
                                    data-testid="data-settings-template-next-btn"
                                    className="RefreshBtn"
                                    disabled={!mappingTargets || mappingTargets.length === 0}
                                >
                                    {t('common.next')} {<ArrowDownIcon className="ArrowIcon" />}
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                    <div ref={fieldsMappingRef} />
                    <Accordion
                        className="MappingAccordion"
                        disabled={!mappingTargets || mappingTargets.length === 0}
                        expanded={expanded === DataSettingsSteps.Mapping}
                        onChange={handleStepChange(DataSettingsSteps.Mapping)}
                    >
                        <AccordionSummary
                            expanded={expanded === DataSettingsSteps.Mapping}
                            id={DataSettingsSteps.Mapping}
                            title={t('data-settings.fields-mapping')}
                            thisStep={2}
                            totalSteps={totalSteps}
                            stepTitle={t('common.accordian-step-title')}
                        />
                        <AccordionDetails>
                            {!dataSettings && <CircularProgress data-testid="CircularProgress" />}
                            {dataSettingFiltered && expanded === DataSettingsSteps.Mapping ? (
                                <Stack direction="column">
                                    <Stack className="MappingAccordionStackTitle" direction="row">
                                        <Typography variant="body1" className="MappingTileDesc">
                                            {t('data-settings.fields-mapping-description')}
                                        </Typography>
                                        <Button
                                            onClick={() => setIsAddCustomFieldOpen(true)}
                                            className="AddCustomFieldBtn"
                                        >
                                            Add a field
                                        </Button>
                                        <RenderSearch setSearchValue={setSearchValue} searchValue={searchValue} />
                                    </Stack>
                                    {MappingItemsComponent}
                                </Stack>
                            ) : null}
                            <Stack marginTop="32px">
                                <Button
                                    onClick={() => {
                                        setDataSettingFiltered(configItems);
                                        scrollTo(dataConfigurationRef, DataSettingsSteps.Config);
                                    }}
                                    className="RefreshBtn"
                                    data-testid="data-settings-mapping-next-btn"
                                >
                                    {t('common.next')} {<ArrowDownIcon className="ArrowIcon" />}
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                    <div ref={dataConfigurationRef} />
                    <Accordion
                        expanded={expanded === DataSettingsSteps.Config}
                        onChange={handleStepChange(DataSettingsSteps.Config)}
                    >
                        <AccordionSummary
                            expanded={expanded === DataSettingsSteps.Config}
                            id={DataSettingsSteps.Config}
                            title={t('data-settings.data-configuration')}
                            thisStep={3}
                            totalSteps={totalSteps}
                            stepTitle={t('common.accordian-step-title')}
                        />
                        <AccordionDetails>
                            {!dataSettings && <CircularProgress data-testid="CircularProgress" />}
                            {expanded === DataSettingsSteps.Config && (
                                <Stack direction="column">
                                    <Stack className="AccordionStackTitle" direction="row">
                                        <RenderSearch setSearchValue={setSearchValue} searchValue={searchValue} />
                                    </Stack>
                                    <Stack className="TabContainer" direction="column" width="100%" spacing={1}>
                                        {dataSettingFiltered.map((item, index) => (
                                            <ConfigItem
                                                key={index}
                                                onAccept={(item: MappingItemProps) =>
                                                    setItemInternal(DataSettingsTypes.Config, { ...item })
                                                }
                                                renderKey={RenderKey}
                                                {...item}
                                            />
                                        ))}
                                        {dataSettingFiltered.length === 0 && noResultComponent}
                                    </Stack>
                                </Stack>
                            )}
                            <Stack marginTop="32px">
                                <Button
                                    onClick={() => {
                                        setDataSettingFiltered(triageItems);
                                        scrollTo(triageRef, DataSettingsSteps.Triage);
                                    }}
                                    className="RefreshBtn"
                                >
                                    {t('common.next')} {<ArrowDownIcon className="ArrowIcon" />}
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                    <div ref={triageRef} />
                    <Accordion
                        expanded={expanded === DataSettingsSteps.Triage}
                        onChange={handleStepChange(DataSettingsSteps.Triage)}
                    >
                        <AccordionSummary
                            expanded={expanded === DataSettingsSteps.Triage}
                            id={DataSettingsSteps.Triage}
                            title={t('data-settings.triage-configuration')}
                            thisStep={4}
                            totalSteps={totalSteps}
                            stepTitle={t('common.accordian-step-title')}
                        />
                        <AccordionDetails>
                            {!dataSettings && <CircularProgress data-testid="CircularProgress" />}
                            {expanded === DataSettingsSteps.Triage && (
                                <Stack direction="column">
                                    <Stack className="AccordionStackTitle" direction="row">
                                        <RenderSearch setSearchValue={setSearchValue} searchValue={searchValue} />
                                    </Stack>
                                    <Stack className="TabContainer" direction="column" width="100%" spacing={1}>
                                        {dataSettingFiltered.length > 0 &&
                                            dataSettingFiltered.map((item, index) => (
                                                <ConfigItem
                                                    key={index}
                                                    onAccept={(item: MappingItemProps) =>
                                                        setItemInternal(DataSettingsTypes.Triage, { ...item })
                                                    }
                                                    renderKey={RenderKey}
                                                    observerValue={
                                                        item.name === 'triage_product_combination'
                                                            ? triageItems.find((i) => i.name === item.observerOn).value
                                                            : null
                                                    }
                                                    {...item}
                                                />
                                            ))}
                                        {dataSettingFiltered.length === 0 && noResultComponent}
                                    </Stack>
                                </Stack>
                            )}
                        </AccordionDetails>
                    </Accordion>
                    <Stack>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    defaultChecked={
                                        configStore.template_config ? configStore.template_config.scop_project : false
                                    }
                                    onChange={(event) => configStore.setProcessToDB(event.target.checked)}
                                />
                            }
                            label={<Typography className="ServiceCoPilotBtn">Service Co-Pilot</Typography>}
                        />
                    </Stack>
                    <AddCustomFieldDialog
                        mappedTargets={mappedTargets}
                        mappingFieldsNames={mappingFieldsNames}
                        fieldsOptions={mappingTargets}
                        open={isAddCustomFieldOpen}
                        onSubmit={onAddCustomField}
                        onClose={() => setIsAddCustomFieldOpen(false)}
                    />
                </Box>
            }
        </Stack>
    );
});
